/**
 * BuildProgressView — what the build actually did, item by item (R3 `TaskSnapshot.build`).
 *
 * GROUPED BY STATUS, ATTENTION-FIRST: `failed` / `blocked` / `dropped` render before the merely
 * in-progress or successful statuses (`groupByStatus`, views.ts) — those are the rows a reader came
 * here to find. A `blocked` item shows what it is blocked BEHIND (`depends_on`, resolved to the
 * blocking item's name) rather than a bare status word, and `dropped` with `error_code:
 * "unsupported_type"` reads "type not supported — excluded" rather than the raw token, because that
 * is also the language `package.excluded` uses for the same drop (PackageView) — one vocabulary for
 * one event described from two places.
 *
 * A PARK IS A PAUSE, NOT A FAILURE — the banner says so and names why (`parkReasonLabel`), separate
 * from any failed/blocked rows underneath it (a build can be parked with zero failures, e.g. out of
 * budget mid-way).
 *
 * Scales to 60+ items the same way `PlanRevisionView` scales to 5000: each status group renders only
 * its first `DEFAULT_VISIBLE_ROWS` items up front, the rest behind a `useState` "show more" toggle
 * (not a native `<details>` — a closed `<details>` still mounts its children into the markup).
 */
import { useState } from "react";
import { AlertTriangle, Ban, Beaker, Check, GitMerge, Loader2, PauseCircle, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { BuildView, WorkItemView } from "@/services/multi-agent/laneV2/types";
import {
  buildCounts,
  buildStatusLabel,
  groupByStatus,
  indexWorkItems,
  parkReasonLabel,
  shortId,
  visibleSlice,
  workItemFailureReason,
  workItemStatusLabel,
} from "./views";

export interface BuildProgressViewProps {
  build: BuildView;
  onViewItemBody?: (workItemId: string) => void;
  className?: string;
}

const STATUS_ICON: Record<string, typeof Check> = {
  failed: XCircle,
  blocked: Ban,
  dropped: Ban,
  leased: Loader2,
  pending: Loader2,
  built: Check,
  validated: Check,
};

const STATUS_STYLE: Record<string, string> = {
  failed: "text-ma-err-text",
  blocked: "text-ma-err-text",
  dropped: "text-muted-foreground",
  leased: "text-primary",
  pending: "text-muted-foreground",
  built: "text-ma-ok-text",
  validated: "text-ma-ok-text",
};

export function BuildProgressView({ build, onViewItemBody, className }: BuildProgressViewProps) {
  const counts = buildCounts(build);
  const groups = groupByStatus(build.items);
  const itemsById = indexWorkItems(build.items);
  const hasRepairChain = (build.predecessors && build.predecessors.length > 0) || build.items.some((i) => i.repair_of);

  return (
    <section aria-labelledby="lanev2-build-title" className={cn("rounded-md border border-border bg-card", className)}>
      <header className="px-3.5 py-2.5">
        <h3 id="lanev2-build-title" className="text-[13px] font-semibold text-foreground">
          Building
          <span className="ml-1.5 font-normal text-muted-foreground">— {buildStatusLabel(build.status)}</span>
        </h3>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {Object.entries(counts).map(([status, n]) => (
            <span key={status} className={cn("rounded bg-muted px-1.5 py-0.5 text-[11px]", STATUS_STYLE[status] ?? "text-muted-foreground")}>
              {n} {workItemStatusLabel(status)}
            </span>
          ))}
        </div>
        {hasRepairChain && (
          <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
            <GitMerge className="h-3 w-3 shrink-0" aria-hidden />
            This build repairs a previous one
            {build.predecessors && build.predecessors.length > 0 ? ` (${build.predecessors.length} earlier attempt${build.predecessors.length === 1 ? "" : "s"})` : ""}
            .
          </p>
        )}
      </header>

      {build.status === "parked" && (
        <p
          role="status"
          className="mx-3.5 mb-3 flex items-center gap-1.5 rounded border border-ma-warn-line px-2.5 py-1.5 text-[12px] text-ma-warn-text"
        >
          <PauseCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {parkReasonLabel(build.park_reason)}
        </p>
      )}

      <div className="border-t border-border">
        {groups.map((group) => (
          <WorkItemGroup key={group.op} status={group.op} items={group.items} itemsById={itemsById} onViewItemBody={onViewItemBody} />
        ))}
        {build.items.length === 0 && <p className="px-3.5 py-3 text-[12px] text-muted-foreground">No items yet.</p>}
      </div>
    </section>
  );
}

function WorkItemGroup({
  status,
  items,
  itemsById,
  onViewItemBody,
}: {
  status: string;
  items: WorkItemView[];
  itemsById: Map<string, WorkItemView>;
  onViewItemBody?: (workItemId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { visible, hiddenCount } = visibleSlice(items);
  const shown = expanded ? items : visible;
  const Icon = STATUS_ICON[status] ?? AlertTriangle;
  return (
    <div className="border-b border-border/60 px-3.5 py-2.5 last:border-b-0">
      <h4 className={cn("flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide", STATUS_STYLE[status] ?? "text-muted-foreground")}>
        <Icon className={cn("h-3.5 w-3.5 shrink-0", status === "leased" && "animate-spin")} aria-hidden />
        {workItemStatusLabel(status)} <span className="font-normal normal-case text-muted-foreground/80">({items.length})</span>
      </h4>
      <ul className="mt-1.5 space-y-1.5">
        {shown.map((item) => (
          <WorkItemRow key={item.work_item_id} item={item} itemsById={itemsById} onViewItemBody={onViewItemBody} />
        ))}
      </ul>
      {!expanded && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
        >
          Show {hiddenCount} more
        </button>
      )}
    </div>
  );
}

function WorkItemRow({
  item,
  itemsById,
  onViewItemBody,
}: {
  item: WorkItemView;
  itemsById: Map<string, WorkItemView>;
  onViewItemBody?: (workItemId: string) => void;
}) {
  const reason = workItemFailureReason(item, itemsById);
  return (
    <li className="text-[13px] leading-snug">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-mono text-[12px] text-foreground">
          {item.metadata_type} · {item.full_name}
        </span>
        {item.kind === "test" && (
          <span className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            <Beaker className="h-3 w-3 shrink-0" aria-hidden />
            test
          </span>
        )}
        {item.repair_of && (
          <span
            title={`Reopened from work item ${item.repair_of} in the previous build`}
            data-lane-v2-repair-of={item.repair_of}
            className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
          >
            <GitMerge className="h-3 w-3 shrink-0" aria-hidden />
            reopened from {shortId(item.repair_of)}
          </span>
        )}
        {typeof item.attempt === "number" && typeof item.max_attempts === "number" && item.max_attempts > 0 && (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            attempt {item.attempt}/{item.max_attempts}
          </span>
        )}
        {item.has_body && onViewItemBody && (
          <button
            type="button"
            onClick={() => onViewItemBody(item.work_item_id)}
            className="ml-auto shrink-0 rounded border border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          >
            View
          </button>
        )}
      </div>
      {reason && <p className="mt-0.5 text-[11px] text-ma-err-text">{reason}</p>}
      {item.manual_prerequisites && item.manual_prerequisites.length > 0 && (
        <p className="mt-0.5 text-[11px] text-ma-warn-text">Needs first: {item.manual_prerequisites.join(", ")}</p>
      )}
    </li>
  );
}
